import React, { useState, useEffect } from 'react';
import { X, Settings as SettingsIcon, Zap, Palette, Bell, Shield, Users, Lock, Bot, BarChart, Brain, Code, MapPin } from 'lucide-react';
import Teams from './Teams';
import BurstModeSettings from './BurstModeSettings';
import PermissionsSettings from './PermissionsSettings';
import ModelsSettings from './ModelsSettings';
import UsageSettings from './UsageSettings';
import PrivacySettings from './PrivacySettings';
import SystemPrompt from './SystemPrompt';
import MCPSettings from './MCPSettings';
import GoogleBusinessSettings from './GoogleBusinessSettings';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAppearance } from '../contexts/AppearanceContext';
import { fetchAllModelsForSettings } from '../services/api';

const Settings = ({ models, currentUser, onClose }) => {
  const [activeTab, setActiveTab] = useState('burst-mode');
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [allModels, setAllModels] = useState(models);
  const [loadingModels, setLoadingModels] = useState(false);
  
  const { 
    canViewTeams, 
    canViewBurstModes, 
    isAdmin,
    isSuperAdmin 
  } = usePermissions();
  
  const { appearanceMode, setAppearanceMode, isSuppleMode, isSuppleLight, isSuppleDark } = useAppearance();
  
  // Define tabs based on permissions
  const tabs = [
    isAdmin && { id: 'models', label: 'Models', icon: Bot },
    canViewBurstModes() && { id: 'burst-mode', label: 'Burst Mode', icon: Zap },
    canViewTeams() && { id: 'team', label: 'Team', icon: Users },
    isAdmin && { id: 'mcp', label: 'MCP Servers', icon: Code },
    { id: 'google-business', label: 'Google Business', icon: MapPin },
    { id: 'memory', label: 'Memory', icon: Brain },
    { id: 'usage', label: 'Usage', icon: BarChart },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    isSuperAdmin && { id: 'privacy', label: 'Privacy & Security', icon: Shield },
    isAdmin && { id: 'permissions', label: 'User Permissions', icon: Lock }
  ].filter(Boolean);
  
  // Set initial tab to first available
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs]);
  
  // Load all models for admin when settings is opened
  useEffect(() => {
    if (isAdmin && !loadingModels) {
      loadAllModels();
    }
  }, [isAdmin]);
  
  const loadAllModels = async () => {
    try {
      setLoadingModels(true);
      const allModelsData = await fetchAllModelsForSettings();
      setAllModels(allModelsData);
    } catch (error) {
      console.error('Failed to load all models:', error);
      // Fall back to filtered models
      setAllModels(models);
    } finally {
      setLoadingModels(false);
    }
  };
  



  const renderTabContent = () => {
    switch (activeTab) {
      case 'models':
        return isAdmin ? (
          <ModelsSettings models={allModels} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>You don't have permission to manage models</p>
          </div>
        );
        
      case 'burst-mode':
        return canViewBurstModes() ? (
          <BurstModeSettings models={allModels} currentUser={currentUser} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>You don't have permission to view burst modes</p>
          </div>
        );

      case 'team':
        return canViewTeams() ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Team Management</h3>
              <button
                onClick={() => setShowTeamsModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Manage Teams
              </button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                Click "Manage Teams" above to create teams, invite members, and manage permissions.
                Teams allow you to collaborate with others and control access to models and features.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-2">Quick Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button 
                    onClick={() => setShowTeamsModal(true)}
                    className="p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-sm">Create Team</div>
                    <div className="text-xs text-gray-600">Start a new team and invite members</div>
                  </button>
                  <button 
                    onClick={() => setShowTeamsModal(true)}
                    className="p-3 text-left border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="font-medium text-sm">Join Team</div>
                    <div className="text-xs text-gray-600">Accept team invitations</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>You don't have permission to view teams</p>
          </div>
        );

      case 'memory':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Memory</h3>
              <p className="text-gray-600">
                {isAdmin
                  ? 'View and manage memory settings for all users. Select a user to view or edit their personal AI preferences.'
                  : 'Set your personal preferences that the AI will remember across all conversations.'}
              </p>
            </div>

            <SystemPrompt
              isOpen={true}
              onClose={() => {}}
              isEmbedded={true}
              isAdmin={isAdmin}
            />
          </div>
        );

      case 'permissions':
        return isAdmin ? (
          <PermissionsSettings />
        ) : null;

      case 'mcp':
        return isAdmin ? (
          <MCPSettings />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>You don't have permission to manage MCP servers</p>
          </div>
        );

      case 'google-business':
        return <GoogleBusinessSettings />;

      case 'usage':
        return <UsageSettings />;

      case 'appearance':
        return (
          <div className={`p-6 ${isSuppleMode ? 'supple-card-frosted' : ''}`}>
            <h3 className={`text-lg font-semibold mb-6 ${
              isSuppleMode ? 'supple-text-primary' : ''
            }`}>Appearance</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className={`text-sm font-medium mb-3 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>Design System</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'default', label: 'Default Mode', description: 'Standard interface with Tailwind styling' },
                    { id: 'supple', label: 'Supple Mode', description: 'Modern design with glass morphism and gradients' }
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setAppearanceMode(mode.id)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        appearanceMode === mode.id
                          ? isSuppleMode
                            ? 'supple-border-gradient-blue supple-card-glass supple-shadow-lg'
                            : 'border-blue-500 bg-blue-50 shadow-md'
                          : isSuppleMode
                            ? 'supple-border-subtle hover:supple-border-gradient-blue hover:supple-shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className={`text-sm font-medium mb-1 ${
                        isSuppleMode ? 'supple-text-primary' : ''
                      }`}>{mode.label}</div>
                      <div className={`text-xs ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                      }`}>{mode.description}</div>
                      {mode.id === 'supple' && (
                        <div className="mt-2 flex gap-1">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-blue-500"></div>
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-400 to-pink-500"></div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Theme</h4>
                <div className="grid grid-cols-3 gap-3">
                  {['Light', 'Dark', 'System'].map((theme) => (
                    <button
                      key={theme}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        theme === 'Light' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{theme}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Font Size</h4>
                <select className="w-full p-2 border rounded-lg">
                  <option>Small</option>
                  <option selected>Medium</option>
                  <option>Large</option>
                  <option>Extra Large</option>
                </select>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Message Density</h4>
                <select className="w-full p-2 border rounded-lg">
                  <option>Compact</option>
                  <option selected>Comfortable</option>
                  <option>Spacious</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case 'notifications':
        return (
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-6">Notifications</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Chat Notifications</h4>
                  <p className="text-sm text-gray-600">Receive notifications for new messages</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Sound Effects</h4>
                  <p className="text-sm text-gray-600">Play sounds for notifications</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Email Notifications</h4>
                  <p className="text-sm text-gray-600">Receive email updates for important events</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        );
        
      case 'privacy':
        return <PrivacySettings />;
        
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg w-full max-w-6xl h-[90vh] flex flex-col appearance-${appearanceMode} ${
        isSuppleMode 
          ? 'supple-card-glass supple-border-subtle backdrop-blur-xl' 
          : 'bg-white'
      }`}>
        <div className={`flex items-center justify-between p-6 ${
          isSuppleMode ? 'supple-border-subtle border-b' : 'border-b'
        }`}>
          <h2 className={`text-2xl font-semibold ${
            isSuppleMode ? 'supple-text-primary' : ''
          }`}>Settings</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isSuppleMode 
                ? 'hover:supple-card-glass-hover supple-text-muted' 
                : 'hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className={`w-64 p-4 ${
            isSuppleMode 
              ? 'supple-card-frosted supple-border-subtle border-r' 
              : 'border-r bg-gray-50'
          }`}>
            <nav className="space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? isSuppleMode
                          ? 'supple-card-glass supple-border-gradient-blue supple-shadow-md supple-text-primary'
                          : 'bg-white text-claude-accent shadow-sm'
                        : isSuppleMode
                          ? 'supple-text-muted hover:supple-card-glass-hover hover:supple-text-primary'
                          : 'text-gray-600 hover:bg-white hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Content */}
          <div className={`flex-1 overflow-y-auto ${
            isSuppleMode ? 'supple-card-frosted' : ''
          }`}>
            {renderTabContent()}
          </div>
        </div>
      </div>
      
      {/* Teams Modal */}
      {showTeamsModal && (
        <Teams
          isOpen={showTeamsModal}
          onClose={() => setShowTeamsModal(false)}
          models={models}
        />
      )}
    </div>
  );
};

export default Settings;